class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.minimumOrderValue = 1900;
    this.minimumOrderUpdateTimer = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.minimumOrderObserver = new MutationObserver(() => this.scheduleMinimumOrderUpdate());
    this.minimumOrderObserver.observe(this, { childList: true, subtree: true });
    this.scheduleMinimumOrderUpdate();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.scheduleMinimumOrderUpdate();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  scheduleMinimumOrderUpdate() {
    window.clearTimeout(this.minimumOrderUpdateTimer);
    this.minimumOrderUpdateTimer = window.setTimeout(() => this.updateMinimumOrderMessage(), 80);
  }

  updateMinimumOrderMessage() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then((cart) => {
        const totals = this.querySelector('.cart-drawer__footer .totals');
        if (!totals) return;

        const footer = totals.closest('.cart-drawer__footer');
        const regularShippingText = footer.querySelector('.zzgl_versand_text:not(.minimum-order-message)');
        let minimumOrderMessage = footer.querySelector('.minimum-order-message');
        const belowMinimum = cart.item_count > 0 && cart.total_price < this.minimumOrderValue;

        if (!belowMinimum) {
          if (minimumOrderMessage) minimumOrderMessage.hidden = true;
          if (regularShippingText) regularShippingText.hidden = false;
          return;
        }

        if (!minimumOrderMessage) {
          minimumOrderMessage = document.createElement('div');
          minimumOrderMessage.className = 'zzgl_versand_text minimum-order-message';
          minimumOrderMessage.setAttribute('role', 'status');
          minimumOrderMessage.setAttribute('aria-live', 'polite');
          minimumOrderMessage.style.whiteSpace = 'pre-line';
          totals.insertAdjacentElement('afterend', minimumOrderMessage);
        }

        minimumOrderMessage.style.setProperty('color', '#b42318', 'important');
        minimumOrderMessage.style.marginTop = '18px';
        minimumOrderMessage.style.lineHeight = '1.25';

        const remainingAmount = (this.minimumOrderValue - cart.total_price) / 100;
        const currency = cart.currency || 'EUR';
        const formattedRemainingAmount = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency,
        }).format(remainingAmount);
        const message = `Der Mindestbestellwert beträgt 19,00 €. Dir fehlen noch\n${formattedRemainingAmount} bis zum Mindestbestellwert.`;

        if (minimumOrderMessage.textContent !== message) minimumOrderMessage.textContent = message;
        minimumOrderMessage.hidden = false;
        if (regularShippingText) regularShippingText.hidden = true;
      })
      .catch(() => {
        // Keep the existing sidecart text unchanged if the cart request fails.
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.scheduleMinimumOrderUpdate();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
